import { ComponentType } from "react";
import { LANGUAGE } from "_constants";
import { reverseDictionary } from "../../utils/reverse-dictionary";
import { ReactComponent as BasketballPlayer } from "./assets/basketball-player.svg";
import { ReactComponent as FreeThrow } from "./assets/free-throw.svg";
import { ReactComponent as FreeThrowScored } from "./assets/basket-success.svg";
import { ReactComponent as FreeThrowMissed } from "./assets/free-throw-missed.svg";
import { ReactComponent as TimeOut } from "./assets/timeout.svg";
import { ReactComponent as Foul } from "./assets/foul.svg";
import { ReactComponent as Whistle } from "./assets/whistle.svg";
import { ReactComponent as Overtime } from "./assets/overtime.svg";
import { ReactComponent as ClockStarted } from "./assets/clock-started.svg";
import { ReactComponent as VideoReview } from "./assets/video-review.svg";
import { ReactComponent as VideoReviewOver } from "./assets/video-review-over.svg";

export enum EVENTS {
  "2 Points" = "1074",
  "3 Points" = "1075",
  "Possession" = "1077",
  "Free Throw" = "1078",
  "Free Throw Scored" = "1079",
  "Free Throw Missed" = "1080",
  "Time Out" = "1081",
  "Foul" = "1086",
  "End Of Quarter" = "1082",
  "End Of Half" = "1083",
  "End Of Match" = "1084",
  "Overtime" = "1085",
  "Quarter 1" = "1087",
  "Quarter 2" = "1088",
  "Quarter 3" = "1089",
  "Quarter 4" = "1090",
  "Half 1" = "1091",
  "Half 2" = "1092",
  "Time Out Over" = "1112",
  "Video Review" = "1123",
  "Clock Started" = "1133",
  "Video Review Over" = "1134",
}

export enum EVENTS_ALB {
  "2 Pikësh" = "1074",
  "3 Pikësh" = "1075",
  "Zotërim të topit" = "1077",
  "Gjuajtje e Lirë" = "1078",
  "Gjuajtja e Lirë u shënua" = "1079",
  "Gjuajtja e Lirë nuk u shënua" = "1080",
  "Mbaroi koha" = "1081",
  "Faull" = "1086",
  "Fundi i Periodës" = "1082",
  "Fundi i Pjesës Parë" = "1083",
  "Fundi i Ndeshjes" = "1084",
  "Koha Shtesë" = "1085",
  "Perioda 1" = "1087",
  "Perioda 2" = "1088",
  "Perioda 3" = "1089",
  "Perioda 4" = "1090",
  "Pjesa Parë" = "1091",
  "Pjesa Dytë" = "1092",
  "Koha e Mbaroi" = "1112",
  "Rishikim Video" = "1123",
}

export const TWO_POINT_CODES = ["1074", "1095"] as const;
export const THREE_POINT_CODES = ["1075", "1094"] as const;
export const SHOT_EVENT_CODES = new Set([
  ...TWO_POINT_CODES,
  ...THREE_POINT_CODES,
  "1079",
]);

type TwoPointCode = (typeof TWO_POINT_CODES)[number];
type ThreePointCode = (typeof THREE_POINT_CODES)[number];

const normalizeCode = (code?: string | number | null) =>
  code === null || code === undefined ? null : String(code);

export const isTwoPointCode = (
  code?: string | number | null
): code is TwoPointCode => {
  const normalized = normalizeCode(code);
  return (
    normalized != null && TWO_POINT_CODES.includes(normalized as TwoPointCode)
  );
};

export const isThreePointCode = (
  code?: string | number | null
): code is ThreePointCode => {
  const normalized = normalizeCode(code);
  return (
    normalized != null &&
    THREE_POINT_CODES.includes(normalized as ThreePointCode)
  );
};

export const isShotEventCode = (code?: string | number | null): boolean => {
  const normalized = normalizeCode(code);
  return normalized != null && SHOT_EVENT_CODES.has(normalized);
};

export const MESSAGES = {
  [LANGUAGE.ENGLISH]: {
    ...reverseDictionary(EVENTS),
    "1095": "2 Points",
    "1094": "3 Points",
    "1118": "Clock Stopped",
    "118": "Clock Stopped",
    "1062": "Possession",
  },
  [LANGUAGE.ALBANIAN]: {
    ...reverseDictionary(EVENTS_ALB),
    "1095": "2 Pikësh",
    "1094": "3 Pikësh",
    "1118": "Mbaroi koha",
    "118": "Mbaroi koha",
    "1062": "Zotërim të topit",
  },
};

export const TIMER_PAUSE_EVENTS = [
  EVENTS["End Of Quarter"],
  EVENTS["End Of Half"],
  EVENTS["End Of Match"],
  EVENTS["Free Throw"],
  EVENTS["Time Out"],
  "1118",
  "118",
  EVENTS["Foul"],
  EVENTS["Time Out Over"],
  EVENTS["Video Review"],
  EVENTS["Video Review Over"],
];

export const TIMER_CONTINUE_EVENTS = [
  EVENTS["Possession"],
  ...TWO_POINT_CODES,
  ...THREE_POINT_CODES,
];

export const ICONS: Record<string, ComponentType<any>> = {
  "1074": BasketballPlayer,
  "1095": BasketballPlayer,
  "1075": BasketballPlayer,
  "1094": BasketballPlayer,
  "1078": FreeThrow,
  "1079": FreeThrowScored,
  "1080": FreeThrowMissed,
  "1081": TimeOut,
  "1086": Foul,
  "1082": Whistle,
  "1083": Whistle,
  "1084": Whistle,
  "1085": Overtime,
  "1087": Overtime,
  "1088": Overtime,
  "1089": Overtime,
  "1090": Overtime,
  "1091": Overtime,
  "1092": Overtime,
  "1118": TimeOut,
  "118": TimeOut,
  "1062": BasketballPlayer,
  "1112": TimeOut,
  "1123": VideoReview,
  "1133": ClockStarted,
  "1134": VideoReviewOver,
};
